import { faFolder } from '@fortawesome/free-regular-svg-icons';
import {
  faChevronRight,
  faHome,
  faLayerGroup,
  faSearch,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import CategoriesService from 'services/Categories.service';
import ServiceCatalogService from 'services/ServiceCatalog.service';
import ServiceGroupService from 'services/ServiceGroup.service';
import SubcategoriesService from 'services/Subcategories.service';

const ServiceCatalogNavigator = ({ onSelect }) => {
  const [level, setLevel] = useState(0); // 0: Group, 1: Catalog, 2: Category, 3: Subcategory
  const [history, setHistory] = useState([]); // Array of selected items [group, catalog, category]
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    loadLevel(0, null);
  }, []);

  useEffect(() => {
    if (searchTerm.length > 2) {
      handleSearch(searchTerm);
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  }, [searchTerm]);

  const loadLevel = async (lvl, parentId) => {
    if (parentId === null && lvl > 0) {
      console.warn(`Cannot load level ${lvl} without parentId`);
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      let response;
      let data = [];

      switch (lvl) {
        case 0:
          response = await ServiceGroupService.list('size=100');
          data = response?.content || [];
          break;
        case 1:
          response = await ServiceCatalogService.listByServiceGroup(parentId);
          // listByServiceGroup may return an array or paginated object
          if (Array.isArray(response)) {
            data = response;
          } else if (response?.content && Array.isArray(response.content)) {
            data = response.content;
          } else {
            data = [];
          }
          break;
        case 2:
          response = await CategoriesService.listByServiceCatalog(
            parentId,
            'size=100'
          );
          data = response?.content || [];
          break;
        case 3:
          response = await SubcategoriesService.listByCategory(
            parentId,
            'includeInactives=false'
          );
          console.log('Subcategories response:', {
            parentId,
            response,
            isArray: Array.isArray(response)
          });
          // listByCategory returns paginated content
          if (Array.isArray(response)) {
            data = response;
          } else if (response?.content && Array.isArray(response.content)) {
            data = response.content;
          } else {
            console.warn('Unexpected subcategories response format:', response);
            data = [];
          }
          break;
        default:
          break;
      }
      console.log(`Level ${lvl} loaded:`, {
        parentId,
        itemCount: data.length,
        data
      });
      setItems(data);
    } catch (error) {
      console.error('Error loading service catalog level', {
        level: lvl,
        parentId,
        error,
        errorMessage: error?.message
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async term => {
    setIsSearching(true);
    setLoading(true);
    try {
      // Search subcategories - try both 'name' and 'searchTerm' parameters
      const searchParams = new URLSearchParams({
        name: term,
        size: 50
      }).toString();

      const response = await SubcategoriesService.list(searchParams);
      console.log('Search response:', {
        term,
        response,
        responseType: typeof response
      });

      // Handle paginated response
      let results = [];
      if (Array.isArray(response)) {
        results = response;
      } else if (response && typeof response === 'object') {
        // Check if it's an error response
        if (response.status && response.status !== 200) {
          console.error('Search API error:', response);
          results = [];
        } else if (response.content && Array.isArray(response.content)) {
          results = response.content;
        } else if (response.content === undefined && response.id) {
          // Single item response
          results = [response];
        }
      }

      console.log('Search results:', results);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching subcategories', {
        term,
        error,
        errorMessage: error.message
      });
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = item => {
    if (level < 3) {
      const newHistory = [...history, item];
      setHistory(newHistory);
      const newLevel = level + 1;
      setLevel(newLevel);
      loadLevel(newLevel, item.id);
    } else {
      if (onSelect) onSelect(item, history);
    }
  };

  const handleSearchResultSelect = item => {
    // Build history from DTO fields if available
    // The subcategory is nested inside item.subcategory
    let history = null;
    if (
      item.serviceGroupId &&
      item.serviceGroupName &&
      item.catalogId &&
      item.catalogName &&
      item.categoryId &&
      item.categoryName &&
      item.subcategory
    ) {
      history = [
        {
          id: item.serviceGroupId,
          name: item.serviceGroupName
        },
        {
          id: item.catalogId,
          name: item.catalogName
        },
        {
          id: item.categoryId,
          name: item.categoryName
        }
      ];
      // Pass the full item (with subcategory nested) to onSelect
      if (onSelect) onSelect(item, history);
    } else if (onSelect) {
      // Fallback: pass item as-is if structure is different
      onSelect(item, null);
    }
  };

  const navigateToLevel = targetLevel => {
    if (targetLevel < 0 || targetLevel >= level) return;

    const newHistory = history.slice(0, targetLevel);
    setHistory(newHistory);
    setLevel(targetLevel);

    const parentId = targetLevel > 0 ? newHistory[targetLevel - 1].id : null;
    loadLevel(targetLevel, parentId);
  };

  const getBreadcrumbs = () => {
    return (
      <div className="d-flex align-items-center mb-3 text-secondary small">
        <span
          role="button"
          className={`me-2 ${
            level === 0 ? 'fw-bold text-primary' : 'text-decoration-none'
          }`}
          onClick={() => navigateToLevel(0)}
        >
          <FontAwesomeIcon icon={faHome} className="me-1" />
          Início
        </span>

        {history.map((item, index) => (
          <React.Fragment key={item.id}>
            <FontAwesomeIcon icon={faChevronRight} className="fs--2 me-2" />
            <span
              role="button"
              className={`me-2 ${
                index + 1 === level ? 'fw-bold text-primary' : ''
              }`}
              onClick={() => navigateToLevel(index + 1)}
            >
              {item.name}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderItemCard = item => {
    const isLeaf = level === 3;

    if (isLeaf) {
      // List style for subcategories
      return (
        <div
          key={item.id}
          className="d-flex align-items-center justify-content-between p-3 mb-2 mx-auto bg-body-emphasis border rounded cursor-pointer hover-shadow"
          onClick={() => handleSelect(item)}
          style={{ transition: 'all 0.2s', width: '95%' }}
        >
          <div>
            <div className="fw-bold text-body-emphasis">{item.name}</div>
            <div className="small text-muted">Serviço</div>
          </div>
          {item.typeTicket && (
            <div className="ms-2 bg-body-emphasis border rounded px-2 py-1 fs-11 text-body-emphasis">
              {item.typeTicket.name}
            </div>
          )}
        </div>
      );
    }

    // Card style for Groups, Catalogs, Categories
    let icon = faFolder;
    if (level === 0) icon = faUsers;
    if (level === 2) icon = faLayerGroup;

    return (
      <Col xs={12} md={6} lg={6} key={item.id} className="mb-3">
        <Card
          className="h-100 cursor-pointer hover-shadow"
          onClick={() => handleSelect(item)}
        >
          <Card.Body className="d-flex align-items-center p-3">
            <div
              className="bg-body-emphasis p-3 rounded-circle me-3 d-flex align-items-center justify-content-center"
              style={{ width: '48px', height: '48px' }}
            >
              <FontAwesomeIcon icon={icon} className="text-primary" />
            </div>
            <div>
              <h6 className="mb-0 fw-bold">{item.name}</h6>
              <small className="text-muted">
                {level === 0
                  ? 'Grupo de Serviço'
                  : level === 1
                  ? 'Catálogo'
                  : level === 2
                  ? 'Categoria'
                  : 'Subcategoria'}
              </small>
            </div>
            <div className="ms-auto">
              <FontAwesomeIcon icon={faChevronRight} className="text-300" />
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <div className="service-catalog-navigator">
      <div className="mb-4">
        <Form.Group className="position-relative">
          <Form.Control
            type="text"
            placeholder="Pesquisar serviço (ex: senha, impressora)..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="ps-4"
          />
          <FontAwesomeIcon
            icon={faSearch}
            className="position-absolute text-400"
            style={{ top: '50%', left: '12px', transform: 'translateY(-50%)' }}
          />
        </Form.Group>
      </div>

      {!isSearching && getBreadcrumbs()}

      {loading ? (
        <Flex justifyContent="center" className="py-5">
          <Spinner animation="border" variant="primary" />
        </Flex>
      ) : isSearching ? (
        <div className="search-results">
          <h6 className="mb-3 text-muted small fw-bold">
            RESULTADOS ENCONTRADOS ({searchResults.length})
          </h6>
          {searchResults.length > 0 ? (
            searchResults.map(item => (
              <div
                key={item.subcategory?.id || item.id}
                className="d-flex align-items-center justify-content-between p-3 mb-2 bg-body-emphasis border rounded cursor-pointer hover-shadow"
                onClick={() => handleSearchResultSelect(item)}
              >
                <div>
                  <div className="fw-bold text-body-emphasis">
                    {item.subcategory.name}
                  </div>
                  <Flex alignItems="center" className="small text-muted">
                    <FontAwesomeIcon icon={faFolder} className="me-1" />
                    {`${item.serviceGroupName} > ${item.catalogName} > ${
                      item.categoryName
                    } > ${item.subcategory?.name || ''}`}
                  </Flex>
                </div>
                {(item.subcategory?.typeTicket || item.typeTicket) && (
                  <div className="ms-2 bg-body-emphasis border rounded px-2 py-1 fs-11 text-body-emphasis">
                    {(item.subcategory?.typeTicket || item.typeTicket)?.name}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted">
              Nenhum serviço encontrado para "{searchTerm}"
            </div>
          )}
        </div>
      ) : (
        <>
          <h6 className="mb-3 text-uppercase text-600 fs--2 fw-bold">
            {level === 0 && 'Selecione o Grupo de Serviço'}
            {level === 1 && 'Catálogos Disponíveis'}
            {level === 2 && 'Categorias'}
            {level === 3 && 'Selecione o Serviço'}
          </h6>

          <Row>
            {items.length > 0 ? (
              items.map(item => renderItemCard(item))
            ) : (
              <Col xs={12}>
                <div className="text-center py-4 text-muted">
                  Nenhum item encontrado nesta seção.
                </div>
              </Col>
            )}
          </Row>
        </>
      )}
    </div>
  );
};

ServiceCatalogNavigator.propTypes = {
  onSelect: PropTypes.func
};

export default ServiceCatalogNavigator;
